import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { memberApi, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-brand-elevated border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

export const NewTopicDialog = ({ categories, defaultCategoryId = "", onClose, onCreated }) => {
  const [categoryId, setCategoryId] = useState(defaultCategoryId || (categories[0]?.id ?? ""));
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  // keep a valid category selected once categories/defaultCategoryId arrive
  useEffect(() => {
    if (!categoryId && (defaultCategoryId || categories[0]?.id)) {
      setCategoryId(defaultCategoryId || categories[0].id);
    }
  }, [categories, defaultCategoryId, categoryId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!categoryId) {
      toast.error("Please choose a category");
      return;
    }
    setLoading(true);
    try {
      const { data } = await memberApi.post("/forum/topics", { category_id: categoryId, title, body });
      toast.success("Topic posted");
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not post topic");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="new-topic-dialog">
      <div className="bg-brand-surface border border-white/10 rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">Start a new topic</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="new-topic-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Category</label>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls} data-testid="new-topic-category">
              {categories.map((c) => <option key={c.id} value={c.id} className="bg-brand-elevated">{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="new-topic-title" placeholder="What's it about?" />
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Message</label>
            <textarea required rows={6} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} data-testid="new-topic-body" placeholder="Share your thoughts…" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={loading || !categoryId} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="new-topic-submit">
              {loading ? "Posting…" : "Post Topic"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};
